import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useInitialPageSize } from '@/hooks/use-server-table';
import { useIsProtected } from '@/state/store/auth/use-is-protected';
import { createRoleTableColumns } from '../../components/role-table-columns/role-table-columns';
import { RoleFormDialog } from '../../components/role-form-dialog/role-form-dialog';
import { RolePermissionsDialog } from '../../components/role-permissions-dialog/role-permissions-dialog';
import { RoleUsersDialog } from '../../components/role-users-dialog/role-users-dialog';
import { useOrganizationRoles } from '../../hooks/use-roles';
import { OrganizationRoleRow } from '../../types/role.types';
import { PageHeader } from '@/components/core';

/**
 * Every role of the project, like the Blocks OS roles page: create a system role, rename it, choose
 * what it grants in this organization, and (admins only) see who holds it here. Roles can't be deleted
 * here. Head of Department roles and workflow-operator are listed read-only. People are given roles from the
 * directory's role dialog, never here.
 */
export const RoleManagerPage = () => {
  const { t } = useTranslation();
  const canCreate = useHasPermission(HRM_PERMISSIONS.SETTINGS.ROLES_CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.SETTINGS.ROLES_UPDATE);
  // Who holds a role is for the organization's admins only, not for everyone who may view roles.
  const canViewUsers = useIsProtected({ roles: ['admin', 'super_admin'] }).isProtected;

  const initialPageSize = useInitialPageSize(15);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: initialPageSize });
  const [formState, setFormState] = useState<{ open: boolean; row: OrganizationRoleRow | null }>({
    open: false,
    row: null,
  });
  const [usersRow, setUsersRow] = useState<OrganizationRoleRow | null>(null);
  const [permissionsRow, setPermissionsRow] = useState<OrganizationRoleRow | null>(null);

  const { data: rows = [], isLoading, error } = useOrganizationRoles();

  const columns = createRoleTableColumns({
    t,
    onUsers: setUsersRow,
    onPermissions: setPermissionsRow,
    onEdit: (row) => setFormState({ open: true, row }),
    canViewUsers,
    canUpdate,
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-1">
        <PageHeader
          title={t('ROLE_MANAGER')}
          actions={
            canCreate
              ? [
                  {
                    label: t('CREATE_ROLE'),
                    icon: <Plus className="size-5" />,
                    onClick: () => setFormState({ open: true, row: null }),
                  },
                ]
              : []
          }
        />
        <p className="text-sm text-muted-foreground">{t('ROLE_MANAGER_DESCRIPTION')}</p>
      </div>

      <AdvanceDataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{ ...pagination, totalCount: rows.length }}
        onPaginationChange={setPagination}
        getRowId={(row) => row.role.slug}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <RoleFormDialog
        open={formState.open}
        onOpenChange={(open) => setFormState((prev) => ({ open, row: open ? prev.row : null }))}
        row={formState.row}
      />

      <RoleUsersDialog
        open={canViewUsers && Boolean(usersRow)}
        onOpenChange={(open) => !open && setUsersRow(null)}
        row={usersRow}
      />

      <RolePermissionsDialog
        open={Boolean(permissionsRow)}
        onOpenChange={(open) => !open && setPermissionsRow(null)}
        row={permissionsRow && !canUpdate ? { ...permissionsRow, readOnly: true } : permissionsRow}
      />
    </div>
  );
};
